import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    type MatasPlusPointInfoModel,
    type PointInfoViewModel,
    PointInfoModelDiscriminator as PointInfoViewModelpointInfoDiscriminator,
    type StandardPointInfoModel
} from 'autogen/swagger/all';
import { MatasPlusPoints, StandardPoints as StandardPointsTrans } from 'autogen/translation-keys/trans-website-points';
import { Typography } from 'components/generic/typography';
import { format } from 'modules/helpers/strings';
import { SubscriptionContext } from '../hooks/context';

export type Points = {
    points: PointInfoViewModel;
    overwritePoints?: PointInfoViewModel;
};

export type StandardPoints = {
    pointsModel: StandardPointInfoModel;
    overwritePoints?: StandardPointInfoModel;
};

export type PlusPoints = {
    pointsModel: MatasPlusPointInfoModel;
    overwritePoints?: MatasPlusPointInfoModel;
};

export function PointText({ text, ...restProps }: { text: string }) {
    // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
    return <Typography.Body {...restProps} as="span" dangerouslySetInnerHTML={{ __html: text }} />;
}

export function StandardPoints({ pointsModel, overwritePoints }: StandardPoints) {
    const { t } = useTranslation();
    const { subscriptionActive } = useContext(SubscriptionContext);
    const points = subscriptionActive && overwritePoints ? overwritePoints.points : pointsModel.points;
    const pointsText = format(t(StandardPointsTrans))(points);

    return <PointText text={pointsText} />;
}

function PlusPoints({ pointsModel, overwritePoints }: PlusPoints) {
    const { subscriptionActive } = useContext(SubscriptionContext);
    const { t } = useTranslation();
    const pointsBefore =
        subscriptionActive && overwritePoints ? overwritePoints.pointsBefore : pointsModel.pointsBefore;
    const pointsAfter = subscriptionActive && overwritePoints ? overwritePoints.pointsAfter : pointsModel.pointsAfter;
    const pointsText = format(t(MatasPlusPoints))(pointsBefore, pointsAfter);

    return <PointText text={pointsText} />;
}

export default function Points({ points, overwritePoints }: Points) {
    // overwritePoints' type is overwritten. This is because the discriminator only tells os the type of "points",
    // but we know "points" and "overwritePoints" always uses the same model.

    return PointInfoViewModelpointInfoDiscriminator(
        points.pointInfo,
        (model) => (
            <StandardPoints
                pointsModel={model}
                overwritePoints={overwritePoints?.pointInfo as StandardPoints['overwritePoints']}
            />
        ),
        (model) => (
            <PlusPoints
                pointsModel={model}
                overwritePoints={overwritePoints?.pointInfo as PlusPoints['overwritePoints']}
            />
        ),
        () => <></>
    );
}
